import { Injectable } from '@angular/core';
import { ChangeTyped, NO_LIMIT_SEARCH_PARAMS, QueryService, SearchResult } from '@damofire/data-commons';
import { cloneDeep } from 'lodash-es';
import { combineLatest, Observable, of, startWith } from 'rxjs';
import { TypedDesQuery } from '../des.dto';
import { ObservableNoteService } from './note-observable.service';
import { Note, Noteable } from './note.dto';

@Injectable()
export abstract class ObservableNoteableService<T extends Noteable> {
  protected abstract readonly SEARCH_FIELDS: string[];
  protected abstract readonly notesFilterFn: (note: Note, noteable: Noteable) => boolean;
  protected abstract noteableSearch(id: string, sQuery: TypedDesQuery): Observable<SearchResult<T>>;

  protected constructor(protected readonly observableNoteService: ObservableNoteService) {}

  protected searchByInspection(divisionId: string, q: TypedDesQuery, forceNoteInspectionId?: string): Observable<SearchResult<T>> {
    q.filters ??= {};
    const assetId = q.filters['asset._id'];
    const inspectionId = q.filters['inspection._id'] || forceNoteInspectionId;
    let noteSub: Observable<Note[]>;

    if (inspectionId) {
      noteSub = this.observableNoteService.observeNotes(divisionId, inspectionId.toString(), assetId?.toString()).pipe(startWith([]));
    } else {
      noteSub = of([]);
    }
    return this.combineNotesSearch(divisionId, q, noteSub);
  }

  protected searchByDivision(divisionId: string, q: TypedDesQuery): Observable<SearchResult<T>> {
    const noteSub = this.observableNoteService.observeDivisionNotes(divisionId).pipe(startWith([]));
    return this.combineNotesSearch(divisionId, q, noteSub);
  }

  private combineNotesSearch(divisionId: string, q: TypedDesQuery, noteSub: Observable<Note[]>): Observable<SearchResult<T>> {
    return new Observable<SearchResult<T>>((observer) => {
      const noteableSub: Observable<SearchResult<T>> = this.noteableSearch(divisionId, { ...q, ...NO_LIMIT_SEARCH_PARAMS, searchPhrase: null });

      const mergedSub = combineLatest([noteSub, noteableSub]).subscribe(([notes, noteableSearch]) => {
        const noteables: T[] = cloneDeep(noteableSearch?.result) || [];
        if (notes?.length && noteables?.length) {
          noteables.forEach((noteable) => this.applyNotes(noteable, notes));
        }

        observer.next(QueryService.searchCollection<T>(noteables, q, this.SEARCH_FIELDS));
      });

      observer.add(mergedSub);
    });
  }

  protected combineNoteableNotes(noteSub: Observable<Note[]>, noteableSub: Observable<ChangeTyped<T>>): Observable<ChangeTyped<T>> {
    return new Observable<ChangeTyped<T>>((observer) => {
      const mergedSub = combineLatest([noteSub, noteableSub]).subscribe(([notes, noteable]) => {
        const noteableClone = cloneDeep(noteable);
        if (notes?.length && noteableClone && noteableClone.changeType !== 'removed') {
          this.applyNotes(noteableClone, notes);
        }

        observer.next(noteableClone);
      });

      observer.add(mergedSub);
    });
  }

  protected combineNoteablesNotes(noteSub: Observable<Note[]>, noteablesSub: Observable<ChangeTyped<T>[]>): Observable<ChangeTyped<T>[]> {
    return new Observable<ChangeTyped<T>[]>((observer) => {
      const mergedSub = combineLatest([noteSub, noteablesSub]).subscribe(([notes, noteables]) => {
        const noteablesClone = cloneDeep(noteables);
        if (notes?.length && noteablesClone?.length) {
          noteablesClone.forEach((noteable) => {
            if (noteable.changeType !== 'removed') {
              this.applyNotes(noteable, notes);
            }
          });
        }
        observer.next(noteablesClone);
      });

      observer.add(mergedSub);
    });
  }

  private applyNotes(noteable: Noteable, notes: Note[]): void {
    noteable.notes = [];
    notes
      .filter((note) => this.notesFilterFn(note, noteable))
      .forEach((foundNote) => {
        noteable.notes.push(foundNote);
        noteable.notesCount = noteable.notes.length;
      });
  }
}
